import React, { createContext, useState } from "react"
import { uuid } from "utils/uuid"
import { isDirectChild } from "app/regions/utils/block-traversal"

export const BlockContext = createContext()

const findTargetBlock = (blocks, block_id) =>
  blocks.findIndex(block => block.block_id === block_id)

/**
 * Given an array of blocks, this function will assign each block with a unique
 * `block_id` and reassign their parent-child relationships.
 *
 * @param {Array} blocks
 * @returns {Array}
 */
export function createUniqueBlocks(blocks) {
  let changedIds = []

  const uniqueBlocks = blocks.map(block => {
    const newBlockId = uuid()
    changedIds.push({ from: block.block_id, to: newBlockId })
    return { ...block, block_id: newBlockId }
  })

  const blocksToAdd = uniqueBlocks.map(block => {
    const match = changedIds.find(id => id.from === block.parent_id)
    if (match) return { ...block, parent_id: match.to }
    return { ...block }
  })

  return blocksToAdd
}

/**
 * The context provider for "blocks", an array of objects that correspond to the
 * elements inside a region. The methods supplied by this provider are very
 * low-level; it is recommended to use `useBlocks` for a friendlier API.
 */

export function BlockProvider({ children, initialBlocks }) {
  const [blocks, setBlocks] = useState(initialBlocks)

  /**
   * Updates a single item in the array of blocks.
   *
   * @param {Object} newData
   */

  const dispatch = newData => {
    setBlocks(previous => {
      const blocks = [...previous]
      const target = findTargetBlock(blocks, newData.block_id)

      if (blocks[target]) {
        blocks[target] = { ...blocks[target], ...newData }
        return blocks
      } else {
        return [...blocks, newData]
      }
    })
  }

  /**
   * Updates the `properties` object inside of a specific block.
   *
   * @param {String} block_id
   * @param {Object} newData
   */

  const updateProperties = (block_id, newData) => {
    setBlocks(previous => {
      const blocks = [...previous]
      const target = findTargetBlock(blocks, block_id)

      if (blocks[target]) {
        blocks[target] = {
          ...blocks[target],
          properties: { ...blocks[target].properties, ...newData }
        }
      }

      return blocks
    })
  }

  /**
   * Overwrites all the blocks in context with a new array.
   *
   * @param {Array} newBlocks
   */

  const overwriteBlocks = newBlocks => setBlocks(newBlocks)

  /**
   * Replaces the blocks in context with a new array and marks the old ones
   * for deletion (so they'll be deleted on a server request).
   *
   * @param {Array} newBlocks
   */

  const replaceBlocks = newBlocks => {
    const blocksToAdd = createUniqueBlocks(newBlocks)
    const blocksToDestroy = blocks.map(block => ({ ...block, _destroy: true }))
    setBlocks([...blocksToDestroy, ...blocksToAdd])
  }

  /**
   * Marks the block with the given `block_id` and its children for deletion.
   *
   * @param {String} block_id
   */

  const deleteBlock = block_id => {
    blocks.map(b => isDirectChild(b, block_id) && deleteBlock(b.block_id))
    dispatch({ block_id, _destroy: true })
  }

  return (
    <BlockContext.Provider
      value={{
        blocks,
        dispatch,
        updateProperties,
        overwriteBlocks,
        replaceBlocks,
        deleteBlock
      }}
    >
      {children}
    </BlockContext.Provider>
  )
}
